import { useState, useRef, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

type AudioPlayerProps = {
  audioSrc: string | null;
};

export function AudioPlayer({ audioSrc }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [audioCurrent, setAudioCurrent] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  useEffect(() => {
    setAudioPlaying(false);
    setAudioCurrent(0);
    setAudioDuration(0);
  }, [audioSrc]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
    } else {
      el.pause();
    }
  };

  const toggleMute = () => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setAudioMuted(el.muted);
  };

  const seekTo = (evt: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !audioDuration) return;
    const rect = evt.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((evt.clientX - rect.left) / rect.width, 0), 1);
    el.currentTime = ratio * audioDuration;
    setAudioCurrent(el.currentTime);
  };

  const formatClock = (seconds: number) => {
    if (!Number.isFinite(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const audioProgressPct = audioDuration > 0 ? (audioCurrent / audioDuration) * 100 : 0;

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={togglePlay}
        disabled={!audioSrc}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] text-[#0F172B] disabled:opacity-50"
        aria-label={audioPlaying ? "Pause" : "Lecture"}
      >
        {audioPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
      <div className="flex-1">
        <div
          role="slider"
          aria-label="Progression audio"
          aria-valuemin={0}
          aria-valuemax={Math.round(audioDuration)}
          aria-valuenow={Math.round(audioCurrent)}
          onClick={seekTo}
          className="h-2 w-full cursor-pointer rounded-full bg-[#E2E8F0]"
        >
          <div
            className="h-2 rounded-full bg-[#0F172B]"
            style={{ width: `${audioProgressPct}%` }}
          />
        </div>
        <div className="mt-1 flex items-center justify-between text-sm font-normal leading-none text-[#45556C]">
          <span>{formatClock(audioCurrent)}</span>
          <span>{formatClock(audioDuration)}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={toggleMute}
        disabled={!audioSrc}
        className="shrink-0 text-[#0F172B] disabled:opacity-50"
        aria-label={audioMuted ? "Réactiver le son" : "Couper le son"}
      >
        {audioMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
      </button>
      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          preload="metadata"
          className="hidden"
          onPlay={() => setAudioPlaying(true)}
          onPause={() => setAudioPlaying(false)}
          onEnded={() => setAudioPlaying(false)}
          onTimeUpdate={(e) => setAudioCurrent(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration || 0)}
          onVolumeChange={(e) => setAudioMuted(e.currentTarget.muted)}
        />
      )}
    </div>
  );
}
